import { useCallback, useEffect, useRef, useState } from 'react';

import {
  DIALOG_RESPONSE_TIMEOUT,
  DIALOG_RESPONSE_TIMEOUT_IN_SECONDS,
  INACTIVITY_EXCLUDED_PATHS,
  INACTIVITY_TIMEOUT,
} from '~/config/inactivity';
import { inactivityBlock } from '~/utils/inactivityBlockRegistry';
import { useAuth } from './contexts/useAuth';

const INACTIVITY_RECHECK_WHILE_BLOCKED_MS = 60000;

const INACTIVITY_SYNC_STORAGE_KEY = '@NovaEra:inactivitySync';
const INACTIVITY_TAB_ACTIVITY_STORAGE_KEY = '@NovaEra:inactivityTabsActivity';
const INACTIVITY_TAB_ID_STORAGE_KEY = '@NovaEra:inactivityTabId';
const TAB_ACTIVITY_RETENTION_WINDOW =
  INACTIVITY_TIMEOUT + DIALOG_RESPONSE_TIMEOUT;

type TabsActivityRegistry = Record<string, number>;

type InactivitySyncAction =
  | 'show-inactivity-dialog'
  | 'keep-session-active'
  | 'logout-by-inactivity'
  | 'user-activity';

type InactivitySyncMessage = {
  action?: InactivitySyncAction;
  deadline?: number;
  timestamp?: number;
  tabId?: string;
};

const createTabId = () =>
  `tab-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const getCurrentTabId = () => {
  const existingTabId = sessionStorage.getItem(INACTIVITY_TAB_ID_STORAGE_KEY);

  if (existingTabId) {
    return existingTabId;
  }

  const nextTabId = createTabId();

  sessionStorage.setItem(INACTIVITY_TAB_ID_STORAGE_KEY, nextTabId);

  return nextTabId;
};

const readTabsActivityRegistry = (): TabsActivityRegistry => {
  const rawValue = localStorage.getItem(INACTIVITY_TAB_ACTIVITY_STORAGE_KEY);

  if (!rawValue) {
    return {};
  }

  try {
    return JSON.parse(rawValue) as TabsActivityRegistry;
  } catch {
    return {};
  }
};

const writeTabsActivityRegistry = (registry: TabsActivityRegistry) => {
  localStorage.setItem(
    INACTIVITY_TAB_ACTIVITY_STORAGE_KEY,
    JSON.stringify(registry)
  );
};

const pruneTabsActivityRegistry = (
  registry: TabsActivityRegistry,
  now = Date.now()
) => {
  return Object.entries(registry).reduce<TabsActivityRegistry>(
    (accumulator, [tabId, lastActivityAt]) => {
      if (now - lastActivityAt <= TAB_ACTIVITY_RETENTION_WINDOW) {
        accumulator[tabId] = lastActivityAt;
      }

      return accumulator;
    },
    {}
  );
};

const getLatestActivityTimestampFromRegistry = (
  registry: TabsActivityRegistry,
  fallbackTimestamp = Date.now()
) => {
  const activityTimestamps = Object.values(registry);

  if (!activityTimestamps.length) {
    return fallbackTimestamp;
  }

  return Math.max(...activityTimestamps);
};

const getRemainingSeconds = (deadline: number) =>
  Math.max(Math.ceil((deadline - Date.now()) / 1000), 0);

const formatCountdown = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

type UseInactivityLogoutResult = {
  dialogIsOpen: boolean;
  dialogCountdownLabel: string;
  keepSessionActive: () => void;
  logoutByInactivity: () => void;
};

/**
 * Controla o ciclo de inatividade da sessao do usuario.
 *
 * Regras cobertas:
 * - Reinicia o timer ao detectar atividade local.
 * - Sincroniza sinais entre abas para evitar falso positivo.
 * - Exibe dialogo de confirmacao antes do logout final.
 * - Realiza logout em todas as abas quando o limite expira.
 */
export const useInactivityLogout = (
  pathname: string
): UseInactivityLogoutResult => {
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [dialogRemainingSeconds, setDialogRemainingSeconds] = useState(
    DIALOG_RESPONSE_TIMEOUT_IN_SECONDS
  );

  const { signOut } = useAuth();

  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dialogResponseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const dialogCountdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const currentTabIdRef = useRef(getCurrentTabId());
  const dialogIsOpenRef = useRef(false);
  const lastKnownActivityAtRef = useRef(Date.now());

  const isInactivityEnabled = !INACTIVITY_EXCLUDED_PATHS.includes(pathname);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const clearDialogResponseTimer = useCallback(() => {
    if (dialogResponseTimerRef.current) {
      clearTimeout(dialogResponseTimerRef.current);
      dialogResponseTimerRef.current = null;
    }

    if (dialogCountdownTimerRef.current) {
      clearInterval(dialogCountdownTimerRef.current);
      dialogCountdownTimerRef.current = null;
    }
  }, []);

  const closeInactivityDialog = useCallback(() => {
    clearDialogResponseTimer();
    dialogIsOpenRef.current = false;
    setDialogIsOpen(false);
  }, [clearDialogResponseTimer]);

  /**
   * Publica um evento de sincronizacao para outras abas.
   */
  const broadcastInactivityAction = useCallback(
    (action: InactivitySyncAction, deadline?: number, timestamp?: number) => {
      localStorage.setItem(
        INACTIVITY_SYNC_STORAGE_KEY,
        JSON.stringify({
          action,
          deadline,
          timestamp: timestamp ?? Date.now(),
          tabId: currentTabIdRef.current,
        })
      );
    },
    []
  );

  /**
   * Le o registro compartilhado de atividade e remove entradas antigas.
   */
  const getLatestProjectActivityTimestamp = useCallback(() => {
    const registry = pruneTabsActivityRegistry(readTabsActivityRegistry());

    writeTabsActivityRegistry(registry);

    const latestActivityAt = getLatestActivityTimestampFromRegistry(registry);

    lastKnownActivityAtRef.current = latestActivityAt;

    return latestActivityAt;
  }, []);

  /**
   * Atualiza a ultima atividade conhecida da aba atual no registro compartilhado.
   */
  const persistCurrentTabActivity = useCallback((activityAt = Date.now()) => {
    const registry = pruneTabsActivityRegistry(readTabsActivityRegistry(), activityAt);

    registry[currentTabIdRef.current] = activityAt;
    writeTabsActivityRegistry(registry);
    lastKnownActivityAtRef.current = activityAt;

    return activityAt;
  }, []);

  /**
   * Remove a aba atual do registro compartilhado ao sair de uma area monitorada.
   */
  const removeCurrentTabFromRegistry = useCallback(() => {
    const registry = readTabsActivityRegistry();

    if (!registry[currentTabIdRef.current]) {
      return;
    }

    delete registry[currentTabIdRef.current];
    writeTabsActivityRegistry(pruneTabsActivityRegistry(registry));
  }, []);

  const executeLogoutByInactivity = useCallback(() => {
    const user = JSON.parse(localStorage.getItem('@NovaEra:user'));
    closeInactivityDialog();
    removeCurrentTabFromRegistry();
    signOut({ cpf: user?.cpf });
  }, [closeInactivityDialog, removeCurrentTabFromRegistry, signOut]);

  const logoutByInactivity = useCallback(() => {
    broadcastInactivityAction('logout-by-inactivity');
    executeLogoutByInactivity();
  }, [broadcastInactivityAction, executeLogoutByInactivity]);

  const openInactivityDialog = useCallback(
    (deadline: number) => {
      clearDialogResponseTimer();

      if (!isInactivityEnabled) {
        closeInactivityDialog();
        removeCurrentTabFromRegistry();
        return;
      }

      if (inactivityBlock.isBlocked()) {
        closeInactivityDialog();
        return;
      }

      const latestActivityAt = getLatestProjectActivityTimestamp();
      const elapsedSinceLastProjectActivity = Date.now() - latestActivityAt;

      if (elapsedSinceLastProjectActivity < INACTIVITY_TIMEOUT) {
        lastKnownActivityAtRef.current = latestActivityAt;
        return;
      }

      if (deadline <= Date.now()) {
        logoutByInactivity();
        return;
      }

      const updateDialogRemainingSeconds = () => {
        const remainingSeconds = getRemainingSeconds(deadline);

        setDialogRemainingSeconds(remainingSeconds);

        return remainingSeconds;
      };

      updateDialogRemainingSeconds();
      dialogIsOpenRef.current = true;
      setDialogIsOpen(true);
      dialogCountdownTimerRef.current = setInterval(() => {
        updateDialogRemainingSeconds();
      }, 1000);
      dialogResponseTimerRef.current = setTimeout(() => {
        logoutByInactivity();
      }, Math.max(deadline - Date.now(), 0));
    },
    [
      clearDialogResponseTimer,
      closeInactivityDialog,
      getLatestProjectActivityTimestamp,
      isInactivityEnabled,
      logoutByInactivity,
      removeCurrentTabFromRegistry,
    ]
  );

  const scheduleInactivityCheck = useCallback(
    (referenceTimestamp?: number) => {
      if (dialogIsOpenRef.current) {
        return;
      }

      clearInactivityTimer();

      if (!isInactivityEnabled) {
        removeCurrentTabFromRegistry();
        return;
      }

      const latestActivityAt =
        referenceTimestamp ?? getLatestProjectActivityTimestamp();
      const remainingTime = Math.max(
        INACTIVITY_TIMEOUT - (Date.now() - latestActivityAt),
        0
      );

      lastKnownActivityAtRef.current = latestActivityAt;

      inactivityTimerRef.current = setTimeout(() => {
        if (inactivityBlock.isBlocked()) {
          inactivityTimerRef.current = setTimeout(() => {
            scheduleInactivityCheck();
          }, INACTIVITY_RECHECK_WHILE_BLOCKED_MS);
          return;
        }

        const refreshedLatestActivityAt = getLatestProjectActivityTimestamp();
        const refreshedRemainingTime = Math.max(
          INACTIVITY_TIMEOUT - (Date.now() - refreshedLatestActivityAt),
          0
        );

        if (refreshedRemainingTime > 0) {
          scheduleInactivityCheck(refreshedLatestActivityAt);
          return;
        }

        const deadline = Date.now() + DIALOG_RESPONSE_TIMEOUT;

        broadcastInactivityAction(
          'show-inactivity-dialog',
          deadline,
          refreshedLatestActivityAt
        );
        openInactivityDialog(deadline);
      }, remainingTime);
    },
    [
      broadcastInactivityAction,
      clearInactivityTimer,
      getLatestProjectActivityTimestamp,
      isInactivityEnabled,
      openInactivityDialog,
      removeCurrentTabFromRegistry,
    ]
  );

  const keepSessionActiveFromSharedActivity = useCallback(
    (activityAt?: number) => {
      closeInactivityDialog();
      scheduleInactivityCheck(activityAt ?? getLatestProjectActivityTimestamp());
    },
    [
      closeInactivityDialog,
      getLatestProjectActivityTimestamp,
      scheduleInactivityCheck,
    ]
  );

  /**
   * Reinicia o timer local de inatividade.
   *
   * A atividade local passa a valer para todas as abas monitoradas.
   */
  const resetTimer = useCallback(
    (shouldBroadcastActivity = false) => {
      if (!isInactivityEnabled) {
        closeInactivityDialog();
        clearInactivityTimer();
        removeCurrentTabFromRegistry();
        return;
      }

      const activityAt = persistCurrentTabActivity();

      if (dialogIsOpenRef.current) {
        closeInactivityDialog();
      }

      scheduleInactivityCheck(activityAt);

      if (shouldBroadcastActivity) {
        broadcastInactivityAction('user-activity', undefined, activityAt);
      }
    },
    [
      broadcastInactivityAction,
      clearInactivityTimer,
      closeInactivityDialog,
      isInactivityEnabled,
      persistCurrentTabActivity,
      removeCurrentTabFromRegistry,
      scheduleInactivityCheck,
    ]
  );

  const keepSessionActive = useCallback(() => {
    broadcastInactivityAction('keep-session-active');
    closeInactivityDialog();
    resetTimer(true);
  }, [broadcastInactivityAction, closeInactivityDialog, resetTimer]);

  const keepSessionActiveFromSync = useCallback(() => {
    closeInactivityDialog();
    scheduleInactivityCheck();
  }, [closeInactivityDialog, scheduleInactivityCheck]);

  const handleUserActivity = useCallback(() => {
    resetTimer(true);
  }, [resetTimer]);

  useEffect(() => {
    return inactivityBlock.onAllTasksComplete(() => {
      resetTimer(true);
    });
  }, [resetTimer]);

  useEffect(() => {
    const activityEvents = [
      'click',
      'focusin',
      'input',
      'keydown',
      'mousedown',
      'pointerdown',
      'scroll',
      'touchstart',
    ];
    const activityListenerOptions = {
      capture: true,
      passive: true,
    };

    window.addEventListener('load', handleUserActivity);
    activityEvents.forEach((eventName) => {
      document.addEventListener(
        eventName,
        handleUserActivity,
        activityListenerOptions
      );
    });

    resetTimer();

    return () => {
      window.removeEventListener('load', handleUserActivity);
      activityEvents.forEach((eventName) => {
        document.removeEventListener(
          eventName,
          handleUserActivity,
          activityListenerOptions
        );
      });
      clearInactivityTimer();
      clearDialogResponseTimer();
    };
  }, [
    clearDialogResponseTimer,
    clearInactivityTimer,
    handleUserActivity,
    resetTimer,
  ]);

  useEffect(() => {
    if (!isInactivityEnabled) {
      closeInactivityDialog();
      clearInactivityTimer();
      removeCurrentTabFromRegistry();
      return;
    }

    const activityAt = persistCurrentTabActivity();

    scheduleInactivityCheck(activityAt);
  }, [
    clearInactivityTimer,
    closeInactivityDialog,
    isInactivityEnabled,
    pathname,
    persistCurrentTabActivity,
    removeCurrentTabFromRegistry,
    scheduleInactivityCheck,
  ]);

  useEffect(() => {
    if (!dialogIsOpen) {
      clearDialogResponseTimer();
      dialogIsOpenRef.current = false;
    }
  }, [clearDialogResponseTimer, dialogIsOpen]);

  useEffect(() => {
    const syncInactivityAction = (event: StorageEvent) => {
      if (event.key !== INACTIVITY_SYNC_STORAGE_KEY || event.newValue === null) {
        return;
      }

      try {
        const { action, deadline, timestamp, tabId } = JSON.parse(
          event.newValue
        ) as InactivitySyncMessage;

        if (tabId && tabId === currentTabIdRef.current) {
          return;
        }

        if (action === 'keep-session-active') {
          keepSessionActiveFromSync();
        }

        if (action === 'show-inactivity-dialog') {
          openInactivityDialog(deadline ?? Date.now() + DIALOG_RESPONSE_TIMEOUT);
        }

        if (action === 'logout-by-inactivity') {
          executeLogoutByInactivity();
        }

        if (action === 'user-activity') {
          keepSessionActiveFromSharedActivity(timestamp);
        }
      } catch {
        // Ignora mensagens invalidadas ou fora do padrao esperado.
      }
    };

    window.addEventListener('storage', syncInactivityAction);

    return () => {
      window.removeEventListener('storage', syncInactivityAction);
    };
  }, [
    executeLogoutByInactivity,
    keepSessionActiveFromSharedActivity,
    keepSessionActiveFromSync,
    openInactivityDialog,
  ]);

  useEffect(() => {
    const unregisterCurrentTab = () => {
      removeCurrentTabFromRegistry();
    };

    window.addEventListener('beforeunload', unregisterCurrentTab);

    return () => {
      window.removeEventListener('beforeunload', unregisterCurrentTab);
      unregisterCurrentTab();
    };
  }, [removeCurrentTabFromRegistry]);

  return {
    dialogIsOpen,
    dialogCountdownLabel: formatCountdown(dialogRemainingSeconds),
    keepSessionActive,
    logoutByInactivity,
  };
};
